import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { IconRefresh } from '../ui/icons';
import { K } from '../kabinet/atoms';

/**
 * «Синхронизировать» — подтянуть свежие данные по нажатию.
 *
 * ЗАЧЕМ КНОПКА, ЕСЛИ ЕСТЬ РАСПИСАНИЕ. Затем, что человек смотрит
 * на экран ПОСЛЕ того, как что-то сделал: поменял ставку, убрал товар,
 * положил денег. Ждать до ближайшего часа, чтобы увидеть последствие
 * своего же действия, — это не «данные обновляются автоматически»,
 * это «экран врёт, и вы знаете об этом».
 *
 * ОТВЕТ СРАЗУ, РАБОТА В ФОНЕ, ЭКРАН ПОКАЗЫВАЕТ ЕЁ ПО ЧАСТЯМ. Полная
 * выгрузка идёт минутами; держать ради неё HTTP-ответ значит подарить
 * человеку таймаут. Поэтому сервер отвечает «начал», а экран потом
 * СПРАШИВАЕТ, далеко ли зашёл обход, и перечитывает данные на каждой
 * пройденной вехе — не дожидаясь самой долгой части.
 *
 * ЗДЕСЬ БЫЛ ТАЙМЕР НА ПОЛМИНУТЫ, И ОН ВРАЛ. Полминуты хватает только
 * на обход остатков; список кампаний и бюджеты к этому мигу ещё
 * не переписаны — владелец 31.08 увидел ровно это. Довод целиком
 * у `ждать` ниже.
 */
/**
 * ═══════════════════════════════════════════════════════════════════════
 * САМО ДЕЙСТВИЕ — ОТДЕЛЬНО ОТ КНОПКИ, И ЭТО НЕ ЛИШНИЙ СЛОЙ.
 *
 * «Синхронизировать» жмут из двух мест: заметной кнопкой в шапке
 * списка и кнопкой внутри блока «Кампаний пока нет» (ответ 32: у
 * пустоты обязана быть одна кнопка выхода из положения). Вид у них
 * разный — одна заметная, другая тихая по эталону пустоты, — а работа
 * одна и та же.
 *
 * Скопировать мутацию во второе место значит завести две правды
 * о том, что перечитывать после выгрузки. Разойдутся они в тот день,
 * когда добавится третий запрос, и разойдутся молча.
 * ═══════════════════════════════════════════════════════════════════════
 */
/**
 * ═══════════════════════════════════════════════════════════════════════
 * ЧТО ПЕРЕЧИТАТЬ ПОСЛЕ ВЫГРУЗКИ — ОДНИМ СПИСКОМ.
 *
 * Здесь было три ключа: `campaigns`, `overview` и `health`. Последний
 * не совпадал ни с чем: полоса достоверности живёт под `data-health`,
 * и гашение по имени `health` не доставало до неё НИ РАЗУ.
 *
 * Остальные экраны не гасились вовсе. Выгрузка переписывает и товары,
 * и запросы, и разбор кампании — человек, стоящий на любом из них,
 * видел прежние числа и делал вывод про сервис, а не про список ключей.
 *
 * ГАСИМ ПО КОРНЮ. У всех ключей кабинета первый член — имя выгрузки,
 * дальше опознаватели и период; гашение по корню достаёт до всех
 * периодов сразу, а это как раз то, что нужно: выгрузка меняет числа
 * во всех.
 * ═══════════════════════════════════════════════════════════════════════
 */
const ПЕРЕЧИТАТЬ = [
  'campaigns',
  'overview',
  'data-health',
  'products',
  'clusters',
  'breakdown',
  'holds',
  /* ОСТАТКИ НА СЧЕТАХ — ОТДЕЛЬНЫМ КЛЮЧОМ, И БЕЗ НЕГО ПОЧИНКА НЕПОЛНА.
     В таблице бюджет приходит вместе со списком кампаний, а в шапке
     карточки живёт своим запросом `['balance', base, campaignId]`.
     Обход остатков идёт своей очередью рядом с быстрой — и не гасить
     этот ключ значило бы обновить всё, кроме того, ради чего затевали. */
  'balance',
  'campaign-products',
  'cabinets-overview',
  /* ЭТИ ТОЖЕ ПЕРЕПИСЫВАЕТ ДОГОНЯЮЩИЙ ПРОХОД. Кнопка тянет не только
     частые выгрузки: отставшие редкие (кластеры, тепловая карта,
     продажи, заказы, минус-фразы) идут тем же нажатием. Экран, стоящий
     на любом из них, обязан увидеть новое — иначе про кнопку снова
     скажут «нажал, ничего не изменилось», и будут правы. */
  'heat',
  'why-quiet',
  'readiness',
  'journal',
  'minus-rules',
] as const;

/**
 * Сколько ждать конца обхода, прежде чем сдаться.
 *
 * ОБХОД ИДЁТ МИНУТАМИ на кабинете с сотней кампаний. Предел нужен
 * не ради обхода, а ради вкладки: спрашивать состояние вечно у экрана,
 * забытого открытым на ночь, незачем.
 */
const ЖДАТЬ_МС = 6 * 60_000;

/**
 * ═══════════════════════════════════════════════════════════════════════
 * КАК ЧАСТО СПРАШИВАТЬ — ДВА СРОКА, А НЕ ОДИН.
 *
 * 03.09.2026 владелец: «нужно чтобы всё подгружало с первого раза
 * и быстро… информация обновлялась сразу в реальном времени».
 *
 * ЗДЕСЬ СТОЯЛИ ТРИ СЕКУНДЫ, И СТОЯЛИ ПЕРЕД ПЕРВЫМ ВОПРОСОМ. То есть
 * веха, готовая через две секунды, показывалась не раньше пятой,
 * а на медленной сети — не раньше шестой. Три секунды пустого
 * ожидания в начале — половина всего, что человек считает
 * «синхронизация».
 *
 * ЧАСТО, ПОКА ЖДЁМ ГЛАВНОГО; РЕДКО, КОГДА ОСТАЛОСЬ ДОГОНЯЮЩЕЕ.
 * Основная часть обхода — секунды, и её надо поймать вовремя.
 * Догоняющая идёт минутами, человек её уже не ждёт, и спрашивать
 * про неё чаще раза в пять секунд значит стучать в свой же сервер
 * без повода.
 * ═══════════════════════════════════════════════════════════════════════
 */
const СПРАШИВАТЬ_ЧАСТО_МС = 800;
const СПРАШИВАТЬ_РЕДКО_МС = 5_000;

interface СостояниеОбхода {
  running: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  /**
   * Сколько шагов обхода не прошло.
   *
   * НУЛЬ И НЕ-НУЛЬ — РАЗНЫЕ СЛОВА НА ЭКРАНЕ. 31.08.2026 владелец:
   * «сделал синхронизацию, пришлось обновлять бюджет вручную».
   * Обход остатков был пропущен (кабинет занят часовым проходом),
   * а кнопка сказала «Готово: данные обновлены». Сказать «готово»
   * там, где не сделано ничего, — хуже, чем не сказать ничего.
   */
  failed?: number;
  /**
   * Сколько вех обхода пройдено.
   *
   * ЗАЧЕМ ОНИ ЗДЕСЬ. 01.09.2026 владелец: «синхронизация идёт очень
   * долго». Дольше всего едут остатки на счетах — по запросу
   * на кампанию; состав кампаний, статусы и расход готовы через пару
   * секунд после нажатия. Ждать конца всего обхода, чтобы показать
   * готовое, — это и есть «очень долго» на его стороне экрана.
   *
   * Теперь служба считает вехи, а экран перечитывает данные на каждой.
   * Число только растёт и сбрасывается в нуль с началом нового обхода.
   */
  готово?: number;
  /**
   * Готово ли то, что человек ВИДИТ: состав кампаний, статусы, расход,
   * остатки, лимиты. Дальше служба догоняет редкие выгрузки — минутами,
   * и ждать их человеку незачем.
   *
   * ПО ЭТОМУ ПРИЗНАКУ КНОПКА И ОТПУСКАЕТСЯ. Довод целиком — у поля
   * `основноеГотово` в `sync.scheduler.ts`.
   */
  основное?: boolean;
}

export function useSyncNow(base: string, onDone: (text: string) => void) {
  const qc = useQueryClient();
  /* ИДЁТ ЛИ ОБХОД — ОТДЕЛЬНО ОТ `isPending` МУТАЦИИ. Мутация кончается
     через полсекунды, обход идёт минутами; одно за другое принимать
     нельзя, иначе кнопка снова скажет «готово» раньше времени. */
  const [идёт, setИдёт] = useState(false);

  const обновить = () => {
    for (const ключ of ПЕРЕЧИТАТЬ) void qc.invalidateQueries({ queryKey: [ключ] });
  };

  /**
   * ═══════════════════════════════════════════════════════════════════
   * ЖДЁМ КОНЕЦ ОБХОДА, А НЕ ТАЙМЕР.
   *
   * 31.08.2026 владелец: «нажал синхронизировать — ничего не изменилось,
   * показывало 35 кампаний, хотя я много позавершал. Нажал второй раз —
   * снова ничего. Обновил страницу — стало 22. Синхронизация должна
   * работать без обновления страницы. Также не синхронизируются
   * бюджеты».
   *
   * ЗДЕСЬ СТОЯЛ `setTimeout` НА ТРИДЦАТЬ СЕКУНД. Ровно столько уходило
   * на один только обход остатков — а следом шли кампании, статусы,
   * расход и лимиты. Перечитывание попадало в СЕРЕДИНУ работы, брало
   * прежний список и прежние бюджеты, и второго раза не было. Числа
   * менялись в базе, а на экране — только после перезагрузки.
   *
   * Обе половины жалобы — и «кампаний всё 35», и «бюджеты не
   * синхронизируются» — это одна эта строка.
   *
   * Теперь экран спрашивает у службы, далеко ли зашёл обход, и гасит
   * данные на каждой пройденной вехе. Кнопка всё это время крутится:
   * человек видит, что работа идёт, а не гадает.
   * ═══════════════════════════════════════════════════════════════════
   */
  const ждать = async () => {
    setИдёт(true);
    const до = Date.now() + ЖДАТЬ_МС;
    /* Сколько вех мы уже показали. Растёт только вверх. */
    let показано = 0;
    /* Сказали ли уже «выполнено» и отпустили ли кнопку. */
    let отпустили = false;

    try {
      while (Date.now() < до) {
        /* ═══════════════════════════════════════════════════════════
           ПАУЗА ПОСЛЕ ВОПРОСА, А НЕ ПЕРЕД НИМ — И ПЕРВЫЙ ВОПРОС
           УХОДИТ СРАЗУ.

           Пауза стояла в начале цикла, и первый вопрос уходил через
           три секунды после нажатия. Веха, готовая через две, ждала
           до пятой; на медленной сети — до шестой. Владелец 03.09
           мерил это секундомером: «делалась 1-2 минуты».

           А ЧАСТОТА ЗАВИСИТ ОТ ТОГО, ЧЕГО МЫ ЖДЁМ. Пока не пришло
           главное — часто (доли секунды); осталось догоняющее —
           редко: человек его уже не ждёт. */
        let ход: СостояниеОбхода;
        try {
          ход = await api<СостояниеОбхода>(`${base}/sync/status`);
        } catch {
          /* СЕТЬ МОРГНУЛА — НЕ ПОВОД ОБЪЯВЛЯТЬ КОНЕЦ. Спросим снова
             через три секунды; предел ожидания всё равно наступит. */
          continue;
        }

        /* ═══════════════════════════════════════════════════════════
           ПОКАЗЫВАЕМ ГОТОВОЕ, НЕ ДОЖИДАЯСЬ ОСТАЛЬНОГО.

           Служба считает вехи: первая — состав кампаний, статусы
           и расход, вторая — остатки на счетах. Выросло число — значит
           в базе появилось новое, и держать его невидимым незачем.

           ПЕРЕЧИТЫВАЕМ НЕ ЧАЩЕ, ЧЕМ РАСТЁТ ЧИСЛО, а не каждый опрос:
           гасить полтора десятка ключей каждые три секунды всю минуту
           обхода значит устроить своему же серверу маленький налёт. */
        const вех = ход.готово ?? 0;
        if (вех > показано) {
          показано = вех;
          обновить();
        }

        /* ═══════════════════════════════════════════════════════════
           «ВЫПОЛНЕНО» И ОТПУСК КНОПКИ — ПО ОСНОВНОЙ ЧАСТИ ОБХОДА.

           03.09.2026 владелец: «нужно чтобы всё подгружало с первого
           раза и быстро… а не с такими приколами как сейчас».

           Обход делает две разные работы. ОСНОВНАЯ — состав кампаний,
           статусы, расход, остатки, лимиты: ровно то, на что человек
           смотрит, нажимая кнопку, и она готова за секунды.
           ДОГОНЯЮЩАЯ — редкие выгрузки, отставшие от срока: минуты.

           Держать кнопку серой всю догоняющую часть значит заставить
           человека ждать того, что он не просил и не видит. Поэтому
           здесь кнопка отпускается, слово сказано — а цикл идёт
           дальше тихо и перечитывает данные на каждой новой вехе.
           Довод целиком — у `основноеГотово` в службе. */
        if (!отпустили && (ход.основное === true || !ход.running)) {
          отпустили = true;
          обновить();
          setИдёт(false);
          onDone(
            ход.error !== null
              ? `Обновление закончилось с ошибкой: ${ход.error}`
              : (ход.failed ?? 0) > 0
                ? 'Обновление закончилось, но часть выгрузок не прошла — ' +
                  'показал то, что пришло. Нажмите ещё раз через минуту.'
                : /* КОРОТКО — ТАК ПРОСИЛ ВЛАДЕЛЕЦ 02.09.2026:
                     «синхронизирую → синхронизация выполнена».
                     Оба неудачных исхода выше остались подробными:
                     короткое сообщение хорошо для обычного течения
                     дел и никуда не годится для беды. */
                  'Синхронизация выполнена.',
          );
        }

        if (!ход.running) {
          обновить();
          return;
        }

        await new Promise((r) =>
          setTimeout(r, отпустили ? СПРАШИВАТЬ_РЕДКО_МС : СПРАШИВАТЬ_ЧАСТО_МС),
        );
      }

      /* НЕ ДОЖДАЛИСЬ — ГОВОРИМ ЭТО, А НЕ «ГОТОВО». И всё равно
         перечитываем: что успело прийти, человек увидит.

         МОЛЧИМ, ЕСЛИ УЖЕ СКАЗАЛИ «ВЫПОЛНЕНО». Основное пришло, кнопка
         отпущена, человек занят делом — сообщать ему через шесть минут,
         что догоняющая часть не уложилась в предел вкладки, значит
         пугать его тем, чего он не просил. */
      обновить();
      if (!отпустили) {
        onDone('Обновление идёт дольше обычного. Показал то, что уже пришло.');
      }
    } finally {
      setИдёт(false);
    }
  };

  const run = useMutation({
    mutationFn: async () =>
      api<{ message: string; started?: boolean }>(`${base}/sync`, { method: 'POST', body: {} }),
    /* ═══════════════════════════════════════════════════════════════
       ОЖИДАНИЕ НЕ ДЕРЖИТ МУТАЦИЮ, И ЭТО НЕ МЕЛОЧЬ.

       Стояло `await ждать()`, и `run.isPending` оставался истинным
       до самого конца ожидания. Кнопка серая — `run.isPending || идёт`,
       — то есть отпустить её изнутри `ждать` было НЕВОЗМОЖНО: мутация
       держала её сама, всю догоняющую часть обхода.

       ЩЕЛИ МЕЖДУ ДВУМЯ ПРИЗНАКАМИ НЕТ. `ждать` — обычная асинхронная
       функция, и первая её строка (`setИдёт(true)`) выполняется
       СИНХРОННО, до первого `await`. То есть `идёт` встаёт в том же
       такте, в котором падает `isPending`, и React сводит их в одну
       перерисовку. Кнопка не мигает. */
    onSuccess: (r) => {
      onDone(r.message);
      void ждать();
    },
    onError: (e) => onDone(e instanceof Error ? e.message : 'Не вышло'),
  });

  /* КНОПКА СЕРАЯ ВСЁ ВРЕМЯ ОБХОДА, а не полсекунды запроса. Иначе она
     оживает сразу, человек жмёт снова — и получает «уже идёт». */
  return { mutate: run.mutate, isPending: run.isPending || идёт };
}

export function SyncNow({ base, onDone }: { base: string; onDone: (text: string) => void }) {
  const run = useSyncNow(base, onDone);

  return (
    /* ЗАМЕТНОЙ, А НЕ ТИХОЙ. Тихая кнопка среди прочих тихих не находится
       вовсе: владелец попросил её дважды, потому что первый раз она
       выглядела подписью. Эта — единственная на экране, которая что-то
       ЗАПУСКАЕТ, и выглядеть должна соответственно. */
    <button
      className={K.кнопкаСинхронизации}
      data-d="кнопка-синхронизации"
      disabled={run.isPending}
      title="Спросить Wildberries заново: состав кампаний, статусы и расход"
      aria-label={run.isPending ? 'Обновляю' : 'Синхронизировать'}
      onClick={() => run.mutate()}
    >
      {/* ПЯТНАДЦАТЬ, А НЕ ЧЕТЫРНАДЦАТЬ: так в эталоне, и кнопка
          от этого ровно на пиксель шире — сверка намерила 39 против
          наших 38 на 390 и 184,2 против 183,2 на 1440. */}
      <IconRefresh
        size={15}
        style={run.isPending ? { animation: 'rk-spin 1s linear infinite' } : undefined}
      />

      {/* ═══════════════════════════════════════════════════════════════
          НА УЗКОМ ОСТАЁТСЯ ОДИН ЗНАЧОК, И ТАК ЖЕ ДЕЛАЕТ ЭТАЛОН:
          подпись обёрнута в `sc-if value="{{ wide }}"`.

          «Синхронизировать» — пятнадцать знаков рядом с промежутком
          дат; вдвоём они уводили страницу вбок на 390. Значок при этом
          не остаётся загадкой: у кнопки есть `title` и `aria-label`,
          а рядом с ним — единственная синяя кнопка на экране.
          ═══════════════════════════════════════════════════════════ */}
      <span className="rk-only-wide">
        {run.isPending ? 'Обновляю…' : 'Синхронизировать'}
      </span>
    </button>
  );
}
